import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FINANCIAL_OCCURRENCE_QUERY_ERROR_CODES,
  loadFinancialOccurrenceSources,
} from '../services/financialOccurrenceQueryService';
import { isValidCivilDate, resolveFinancialOccurrences } from '../domain/finance';

const requestError = (code) => Object.assign(new Error(code), { code });

export function useFinancialOccurrences({ accountId, startDate, endDate, enabled = true } = {}) {
  const [retryVersion, setRetryVersion] = useState(0);
  const generationRef = useRef(0);
  const validRange = isValidCivilDate(startDate)
    && isValidCivilDate(endDate)
    && startDate <= endDate;
  const validRequest = Boolean(enabled && accountId && validRange);
  const requestKey = useMemo(() => JSON.stringify([
    enabled, accountId || '', startDate || '', endDate || '', retryVersion,
  ]), [enabled, accountId, startDate, endDate, retryVersion]);
  const [state, setState] = useState({
    occurrences: [],
    resolvedRequestKey: null,
    resolvedRange: null,
    resolvedAccountId: null,
    error: null,
  });
  const loading = validRequest && state.resolvedRequestKey !== requestKey;

  useEffect(() => {
    const generation = generationRef.current + 1;
    generationRef.current = generation;
    let active = true;
    const isCurrent = () => active && generationRef.current === generation;

    if (!enabled) {
      setState({
        occurrences: [],
        resolvedRequestKey: requestKey,
        resolvedRange: null,
        resolvedAccountId: null,
        error: null,
      });
      return () => {
        active = false;
        if (generationRef.current === generation) generationRef.current += 1;
      };
    }

    if (!validRequest) {
      const code = accountId
        ? FINANCIAL_OCCURRENCE_QUERY_ERROR_CODES.INVALID_RANGE
        : FINANCIAL_OCCURRENCE_QUERY_ERROR_CODES.ACCOUNT_REQUIRED;
      setState({
        occurrences: [],
        resolvedRequestKey: requestKey,
        resolvedRange: null,
        resolvedAccountId: null,
        error: requestError(code),
      });
      return () => {
        active = false;
        if (generationRef.current === generation) generationRef.current += 1;
      };
    }

    const range = { accountId, startDate, endDate };
    setState((current) => ({ ...current, error: null }));
    Promise.resolve()
      .then(() => loadFinancialOccurrenceSources(range))
      .then((sources) => resolveFinancialOccurrences({ ...sources, startDate, endDate }))
      .then((occurrences) => {
        if (!isCurrent()) return;
        setState({
          occurrences,
          resolvedRequestKey: requestKey,
          resolvedRange: { startDate, endDate },
          resolvedAccountId: accountId,
          error: null,
        });
      })
      .catch((error) => {
        if (!isCurrent()) return;
        setState((current) => ({
          ...current,
          resolvedRequestKey: requestKey,
          error,
        }));
      });

    return () => {
      active = false;
      if (generationRef.current === generation) generationRef.current += 1;
    };
  }, [accountId, startDate, endDate, enabled, validRequest, requestKey]);

  const retry = useCallback(() => setRetryVersion((version) => version + 1), []);
  return {
    occurrences: state.occurrences,
    loading,
    error: state.error,
    resolvedRange: state.resolvedRange,
    resolvedAccountId: state.resolvedAccountId,
    retry,
  };
}


